import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

export interface Transaction {
  id: string;
  profile_id: string;
  type: 'deposit' | 'withdrawal' | 'payment_received' | 'payment_sent' | 'refund' | 'fee';
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  description: string | null;
  related_deal_id: string | null;
  related_payment_schedule_id: string | null;
  payment_method: string | null;
  reference_number: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface UseTransactionsOptions {
  type?: Transaction['type'];
  status?: Transaction['status'];
  limit?: number;
}

export const useTransactions = (options: UseTransactionsOptions = {}) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchTransactions = async () => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('transactions')
        .select('*')
        .eq('profile_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (options.type) {
        query = query.eq('type', options.type);
      }

      if (options.status) {
        query = query.eq('status', options.status);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTransactions((data || []) as Transaction[]);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const createDeposit = async (amount: number, payment_method: string = 'stripe') => {
    try {
      const { data, error } = await supabase.functions.invoke('process-deposit', {
        body: { amount, payment_method },
      });

      if (error) throw error;

      if (data?.client_secret) {
        return {
          client_secret: data.client_secret,
          payment_intent_id: data.payment_intent_id,
        };
      } else {
        throw new Error('Failed to create payment intent');
      }
    } catch (err) {
      console.error('Deposit error:', err);
      toast({
        title: 'Deposit Failed',
        description: err instanceof Error ? err.message : 'Failed to process deposit',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const createWithdrawal = async (amount: number, payout_method_id?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('process-withdrawal', {
        body: { amount, payout_method_id },
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: 'Withdrawal Initiated',
          description: `$${amount} withdrawal is being processed`,
        });
        await fetchTransactions();
        return data;
      } else {
        throw new Error(data.error || 'Withdrawal failed');
      }
    } catch (err) {
      console.error('Withdrawal error:', err);
      toast({
        title: 'Withdrawal Failed',
        description: err instanceof Error ? err.message : 'Failed to process withdrawal',
        variant: 'destructive',
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchTransactions();

    // Set up realtime subscription
    const channel = supabase
      .channel('transaction-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `profile_id=eq.${currentUser?.id}`,
        },
        () => {
          fetchTransactions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id, options.type, options.status, options.limit]);

  return {
    transactions,
    loading,
    error,
    refetch: fetchTransactions,
    createDeposit,
    createWithdrawal,
  };
};